import React from 'react';
import { getDeviceValues } from 'app/util/devices';

import $dictionary from 'app/stores/$dictionary';
import $mitigations from 'app/stores/mitigations/$mitigations';
import $metrics from 'app/stores/metrics/$metrics';
import $notifications from 'app/stores/notifications/$notifications';
import { checkApplication } from '@kentik/ui-shared/alerting/applicationUtils';

import Tooltip from 'core/components/Tooltip';
import Tag from 'core/components/Tag';
import Box from 'core/components/Box';
import Text from 'core/components/Text';
import Flex from 'core/components/Flex';

import SelectedValueButtonCompact from 'app/components/device/SelectedValueButtonCompact';
import NotificationOption from 'app/components/notifications/NotificationOption';

import EventPolicyModel from 'app/stores/alerting/kevent/EventPolicyModel';
import NmsPolicyModel from 'app/stores/alerting/nms/NmsPolicyModel';
import FlowPolicyModel from 'app/stores/alerting/FlowPolicyModel';
import { PERCENT_METRICS, GREEK_METRICS, ALERT_METRIC_OPTIONS } from './constants';
import { getNmsDimensionLabel } from './dimensions';

export function getMetricOption(policyMetric = '', selectedMeasurement = '') {
  const fallbackOption = {
    label: 'Unknown',
    value: policyMetric,
    unit: policyMetric
  };

  let metricOption = ALERT_METRIC_OPTIONS.find((option) => option.value === policyMetric);

  if (!metricOption && selectedMeasurement) {
    const measurementModel = $metrics.measurementModel(selectedMeasurement);

    if (measurementModel) {
      const metricLabel = measurementModel.metricLabel(policyMetric);
      const metricDetails = measurementModel.metricDetails(policyMetric);

      metricOption = {
        value: policyMetric,
        label: metricLabel,
        // currently, we do not support units for metric policies metrics
        unit: metricLabel,
        unitLabel: metricLabel,
        isPercent: metricDetails?.Unit === 'percent'
      };
    }
  }

  if (!metricOption) {
    metricOption = fallbackOption;
  }

  return {
    unitLabel: $dictionary.get(`units.${metricOption.unit}`) || 'unknown',
    isPercent: PERCENT_METRICS.includes(policyMetric),
    canGreek: GREEK_METRICS.includes(policyMetric),
    ...metricOption
  };
}

export function getTopItems({ items, max = 2, itemRenderer, tooltipItemRenderer, showTooltip = true, column }) {
  if (!items?.length > 0) {
    return null;
  }

  const count = items.length;
  const overCount = count > max ? count - max : 0;
  const topItems = items.slice(0, max);

  const tooltipLabel = (
    <Text as="div" muted>
      +{overCount} more
    </Text>
  );

  return (
    <Box>
      {topItems.map((item, idx) => {
        const hasOverCount = idx === max - 1 && overCount > 0;

        return (
          <Flex
            flexDirection={column ? 'column' : undefined}
            key={item.id || item.name || item.label || item.title || idx}
            alignItems={column ? 'flex-start' : 'center'}
            gap={column ? 0 : '4px'}
          >
            {itemRenderer ? itemRenderer(item, idx) : item}
            {showTooltip && hasOverCount && (
              <Tooltip
                content={items.slice(idx + 1).map((overItem, overIdx) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <Box key={overIdx}>{tooltipItemRenderer ? tooltipItemRenderer(overItem, overIdx) : overItem}</Box>
                ))}
              >
                {tooltipLabel}
              </Tooltip>
            )}
            {!showTooltip && hasOverCount && tooltipLabel}
          </Flex>
        );
      })}
    </Box>
  );
}

export function getThresholdMitigationTags(mitigations) {
  const selectedMitigations = $mitigations.platformsCollection
    .getPolicyOptions(mitigations || [])
    .filter((m) => m.selected !== undefined);

  return selectedMitigations.map(({ key, methodName, platformName }) => (
    <Box key={key} mt="4px">
      <Tag mr="4px">{platformName}</Tag>
      {methodName}
    </Box>
  ));
}

export function getNotificationChannelTags(notificationChannels) {
  const selectedValues = notificationChannels || [];
  const channelOptions = $notifications.collection.notificationOptions;
  const selectedChannels = channelOptions.filter((option) => selectedValues.includes(option.value));

  return selectedChannels.map((channel) => (
    <Box mt="4px" key={channel.value}>
      <Tag small style={{ padding: '2px 6px 2px 0px' }}>
        <NotificationOption option={channel} />
      </Tag>
    </Box>
  ));
}

export function getPolicyDevices({ model }) {
  const { all_devices, device_labels, device_name, device_sites, device_types } = model.get('selected_devices');

  if (all_devices) {
    return ['All Data Sources'];
  }

  const deviceValues = getDeviceValues({
    simpleSiteLabelsOnly: true,
    all_devices,
    device_types,
    device_labels,
    device_sites,
    device_name
  });

  return [
    ...deviceValues.get('types'),
    ...deviceValues.get('labels'),
    ...deviceValues.get('sites'),
    ...deviceValues.get('devices')
  ];
}

export function getPolicyDeviceTag(device, tagProps = {}) {
  if (device === 'All Data Sources') {
    return (
      <SelectedValueButtonCompact label="All Data Sources" icon="multi-select" key="allDataSources" {...tagProps} />
    );
  }

  let iconName = null;

  // If device is a label
  if (device.bg) {
    iconName = 'tag';
  }

  // If device is a site
  if (device.activeDevices) {
    iconName = 'map-marker';
  }

  return <SelectedValueButtonCompact icon={iconName} option={device} key={device.value} {...tagProps} />;
}

export function getPolicyMeasurementTag(measurement, tagProps = {}) {
  return (
    <SelectedValueButtonCompact
      option={$metrics.availableLabelMeasurementOptions.find((m) => m.value === measurement)}
      key={measurement}
      {...tagProps}
    />
  );
}

export function getDimensionOption(policyDimension = '', options = {}) {
  const { useChartTypes = false, reconDimensions, getDimensionObject = false } = options;
  const dimensionOptions =
    reconDimensions || (useChartTypes ? $dictionary.flatDimensionOptions : $dictionary.flatFilterFieldOptions);
  const dimensionDisplay = dimensionOptions.find((option) => option.value === policyDimension);

  if (!dimensionDisplay) {
    return `Invalid: ${policyDimension}`;
  }

  if (getDimensionObject) {
    return dimensionDisplay;
  }

  if (dimensionDisplay.group) {
    return `${dimensionDisplay.group} ${dimensionDisplay.label}`;
  }

  return dimensionDisplay.label;
}

export function getFullSelectedMeasurement(selectedMeasurement) {
  return $metrics.availableMeasurementOptions.find(
    (measurement) => measurement.id === selectedMeasurement || measurement.measurement === selectedMeasurement
  );
}

export function getNmsMetricOption(policyMetric = '', measurements = []) {
  const fallbackOption = {
    label: 'Unknown',
    value: policyMetric,
    unit: policyMetric
  };

  let metricOption = ALERT_METRIC_OPTIONS.find((option) => option.value === policyMetric);

  if (!metricOption && measurements.length > 0) {
    // try to find the measurement that fits the metric
    const appropriateMeasurement = measurements.find((measurement) =>
      $metrics.measurementModel(measurement)?.metricDetails(policyMetric)
    );

    if (appropriateMeasurement) {
      const measurementModel = $metrics.measurementModel(appropriateMeasurement);

      if (measurementModel) {
        metricOption = {
          value: policyMetric,
          label: measurementModel.metricLabel(policyMetric),
          // currently, we do not support units for metric policies metrics
          unit: '',
          unitLabel: ''
        };
      }
    }
  }

  if (!metricOption) {
    metricOption = fallbackOption;
  }

  return {
    unitLabel: $dictionary.get(`units.${metricOption.unit}`) || 'unknown',
    ...metricOption,
    isPercent: PERCENT_METRICS.includes(policyMetric),
    canGreek: GREEK_METRICS.includes(policyMetric)
  };
}

export function getNmsDimensionOption(policyDimension = '', measurements = []) {
  const fallbackOption = {
    label: 'Unknown',
    value: policyDimension,
    unit: policyDimension
  };

  // try to find the measurement that fits the dimension
  let dimensionOption = measurements.reduce((a, measurement) => {
    if (a) {
      return a;
    }

    const measurementModel = $metrics.measurementModel(measurement);

    if (measurementModel) {
      const dimension = measurementModel.storage.Dimensions[policyDimension];

      if (dimension) {
        return { ...dimension, label: getNmsDimensionLabel(policyDimension, dimension) };
      }
    }

    return null;
  }, null);

  if (!dimensionOption) {
    dimensionOption = fallbackOption;
  }

  return dimensionOption;
}

// Used in legacy policy form and summary panel
export function getPrimaryMetric(policyMetrics = [], form, application, prefix = '') {
  const primaryMetric = policyMetrics[0];
  const applicationToUse = application || form.getValue('application');
  const isMetricPolicy = checkApplication.isMetric(applicationToUse);

  return (
    primaryMetric &&
    getMetricOption(
      primaryMetric,
      isMetricPolicy ? form.getValue(`${prefix}uiProperties.selectedMeasurement`) : undefined
    ).label
  );
}

// TODO NMS POLICY - this might see some usage and might need updating as we move forward on detail page
export function getMeasurementModelDataFromPolicy(policy) {
  // If we're in a form context, not a deserialization context
  if (policy?.uiProperties?.selectedMeasurement) {
    return {
      fullMeasurement: getFullSelectedMeasurement(policy.uiProperties.selectedMeasurement),
      measurement: policy.uiProperties.selectedMeasurement
    };
  }

  // Otherwise, we're in a deserialization context
  const { filters } = policy;
  const metadataMeasurement = policy?.applicationMetadata?.measurement;
  const filterDimension = $metrics.getAppProtocolDimension(metadataMeasurement, 'km_measurement_name');
  const measurementFilterGroupIndex = filters.filterGroups?.findIndex(
    (group) => group.filters[0]?.filterField === filterDimension
  );

  const measurement =
    metadataMeasurement ||
    (measurementFilterGroupIndex >= 0 && filters.filterGroups[measurementFilterGroupIndex].filters[0].filterValue);

  const fullMeasurement = getFullSelectedMeasurement(measurement);

  return {
    fullMeasurement,
    measurement
  };
}

export function mapKmetricDimensions(policy) {
  const { fullMeasurement, measurement } = getMeasurementModelDataFromPolicy(policy);
  const storageDimensions = fullMeasurement?.storage?.Dimensions || {};
  const storageDimensionKeys = Object.keys(storageDimensions);

  return policy.dimensions
    .map((dimension) => {
      const column = dimension.replace($metrics.getAppProtocolDimension(measurement), '');
      // these dimensions are always secretly injected into kmetrics policies
      if (['km_device_id', 'km_measurement_name'].includes(column)) {
        return null;
      }

      return (
        storageDimensionKeys.find((key) => {
          const { Column, QueryColumn } = storageDimensions[key] || {};
          return Column === column || QueryColumn === column;
        }) || column
      );
    })
    .filter(Boolean);
}

export function getKmetricDimensionLabelForPolicy(policy, dimension) {
  const { fullMeasurement, measurement } = getMeasurementModelDataFromPolicy(policy);
  const baseDimension = dimension.replace($metrics.getAppProtocolDimension(measurement), '');

  if (fullMeasurement) {
    const keyMatch = Object.keys(fullMeasurement.storage.Dimensions).find((key) => {
      const { Column, QueryColumn } = fullMeasurement.storage.Dimensions[key] || {};
      return Column === baseDimension || QueryColumn === baseDimension;
    });
    return keyMatch || baseDimension;
  }

  return baseDimension;
}

export function isNmsPolicy(policy) {
  return checkApplication.isNms(policy?.application);
}

export function getPolicyModelFromAttributes(policyAttributes, deserialize = true) {
  const { application } = policyAttributes || {};

  if (checkApplication.isNms(application)) {
    return new NmsPolicyModel(policyAttributes, { deserialize });
  }

  if (checkApplication.isEvent(application)) {
    return new EventPolicyModel(policyAttributes, { deserialize });
  }

  return new FlowPolicyModel(policyAttributes, { deserialize });
}
